/**
 * CookieAuth - Session metadata + integration status cookies
 */

import {
  SESSION_COOKIE_NAME, INTEGRATIONS_COOKIE_NAME,
  AUTH_DOMAIN, IS_SECURE,
  type IntegrationStatus, type SessionMetadata,
} from './constants'

function cookieBase(): { domain: string; secure: string } {
  return {
    domain: AUTH_DOMAIN ? `domain=${AUTH_DOMAIN};` : '',
    secure: IS_SECURE ? 'secure;' : '',
  }
}

function setCookieRaw(name: string, value: string, expiresInDays: number): void {
  if (typeof document === 'undefined') return
  const expires = new Date(Date.now() + expiresInDays * 86_400_000)
  const { domain, secure } = cookieBase()
  document.cookie = `${name}=${encodeURIComponent(value)}; ${domain} path=/; expires=${expires.toUTCString()}; ${secure} samesite=lax`
}

function getCookieRaw(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function clearCookieRaw(name: string): void {
  if (typeof document === 'undefined') return
  const { domain } = cookieBase()
  document.cookie = `${name}=; ${domain} path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
}

// =============================================================================
// INTEGRATION STATUS COOKIE
// =============================================================================

export function setIntegrationsCookie(status: IntegrationStatus): void {
  setCookieRaw(INTEGRATIONS_COOKIE_NAME, JSON.stringify(status), 1) // 24 hours
}

export function getIntegrationsCookie(): IntegrationStatus | null {
  const raw = getCookieRaw(INTEGRATIONS_COOKIE_NAME)
  if (!raw) return null
  try { return JSON.parse(raw) } catch { return null }
}

export function clearIntegrationsCookie(): void { clearCookieRaw(INTEGRATIONS_COOKIE_NAME) }

/** Check if Xero token is expired based on cached integration status */
export function isXeroExpired(): boolean {
  const status = getIntegrationsCookie()
  if (!status?.xero_connected || !status.xero_expires_at) return false
  return Date.now() >= new Date(status.xero_expires_at).getTime()
}

// =============================================================================
// SESSION METADATA COOKIE
// =============================================================================

function generateDeviceId(): string {
  if (typeof window === 'undefined') return 'server'
  const data = [navigator.userAgent, navigator.language, screen.width, screen.height, new Date().getTimezoneOffset()].join('|')
  let hash = 0
  for (let i = 0; i < data.length; i++) {
    hash = ((hash << 5) - hash) + data.charCodeAt(i)
    hash = hash & hash
  }
  return Math.abs(hash).toString(36)
}

export function setSessionCookie(hubOrigin: string): void {
  const now = new Date().toISOString()
  const session: SessionMetadata = {
    login_time: now, last_activity: now, device_id: generateDeviceId(), hub_origin: hubOrigin,
  }
  setCookieRaw(SESSION_COOKIE_NAME, JSON.stringify(session), 30)
}

export function getSessionCookie(): SessionMetadata | null {
  const raw = getCookieRaw(SESSION_COOKIE_NAME)
  if (!raw) return null
  try { return JSON.parse(raw) } catch { return null }
}

export function updateSessionActivity(): void {
  const session = getSessionCookie()
  if (!session) return
  const updated: SessionMetadata = { ...session, last_activity: new Date().toISOString() }
  setCookieRaw(SESSION_COOKIE_NAME, JSON.stringify(updated), 30)
}

export function clearSessionCookie(): void { clearCookieRaw(SESSION_COOKIE_NAME) }

/** Time since last activity in milliseconds */
export function getInactivityDuration(): number {
  const session = getSessionCookie()
  if (!session) return Infinity
  return Date.now() - new Date(session.last_activity).getTime()
}
